import { useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useApi } from "../../../../hooks/useApi";

/**
 * useGraphicalPeople — the roster the People panel needs.
 *
 * The classic `/overview` payload carries people COUNTS (total / tracked /
 * silent) but not a per-person roster with names and active/idle. That roster
 * lives on `team-analytics-paginated`, the same endpoint the card dashboard's
 * team table uses. Server-side pagination: the hook owns the page cursor,
 * fetches one page at a time, and hands the panel everything the pager needs.
 *
 * NOTE: the payload has `lastActivity` but no `firstActivity`, so the mock's
 * first→last "working window" bar cannot be drawn from this data. The panel
 * shows the active/idle split instead, which is real.
 */
export function useGraphicalPeople(range, member, limit = 16) {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0); // 0-based; the API is 1-based

  const start = range?.start;
  const end = range?.end;

  /* A new window or member filter restarts from the first page. */
  useEffect(() => { setPage(0); }, [start, end, member]);

  useEffect(() => {
    if (!start || !end) return undefined;
    let live = true;
    setLoading(true);

    const fmt = (d) => {
      const x = new Date(d);
      return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
    };
    const params = new URLSearchParams({
      startDate: fmt(start), endDate: fmt(end),
      page: String(page + 1), limit: String(limit), sortOrder: "desc",
    });
    if (member) params.append("userId", member);

    api
      .get(`/api/v1/live-analytics/team-analytics-paginated?${params.toString()}`)
      .then((res) => {
        if (!live) return;
        const data = res.data?.data || {};
        const list = (data.teamAnalytics || []).map((m) => {
          const am = m.activeMinutes || 0;
          const im = m.idleMinutes || 0;
          return {
            userId: m.userId,
            name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Member",
            position: m.position || "Team Member",
            activeMinutes: am,
            idleMinutes: im,
            totalMinutes: am + im,
            lastActivity: m.lastActivity || null,
            hasData: (m.sessions || 0) > 0 || am + im > 0,
          };
        });
        setRows(list);
        /* The endpoint reports the roster size at pagination.total —
           the old totalCount/total probes hit nothing and fell back to
           the page's own 8 rows, which kept the pager permanently hidden. */
        setTotal(data.pagination?.total ?? data.totalCount ?? data.total ?? list.length);
      })
      .catch(() => {
        if (live) { setRows([]); setTotal(0); }
      })
      .finally(() => { if (live) setLoading(false); });

    return () => { live = false; };
  }, [api, start, end, member, limit, page]);

  /* Excel export — the same flow the card dashboard's Team Performance
     table has always had: fetch ALL rows (no pagination) as JSON, build
     the workbook client-side. Same columns, same filename shape. */
  const exportPeople = useCallback(async () => {
    try {
      toast.loading("Preparing export…", { id: "gph-export" });
      const fmt = (d) => {
        const x = new Date(d);
        return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
      };
      const params = new URLSearchParams({ startDate: fmt(start), endDate: fmt(end), sortOrder: "desc" });
      if (member) params.append("userId", member);
      const res = await api.get(`/api/v1/live-analytics/team-analytics-export?${params.toString()}`);
      if (!res.data?.success) throw new Error(res.data?.message || "Export failed");
      const { teamAnalytics = [], exportTime } = res.data.data || {};
      const excelData = teamAnalytics.map((m, i) => ({
        "S.No": i + 1,
        "Member Name": `${m.firstName || ""} ${m.lastName || ""}`.trim(),
        "Email": m.email,
        "Position": m.position,
        "Active Hours": m.activeTime,
        "Idle Hours": m.idleTime,
        "Total Hours": m.hours,
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws["!cols"] = [{ wch: 8 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, "Team Performance");
      const ts = new Date(exportTime || Date.now()).toISOString().replace(/[:.]/g, "-").slice(0, -5);
      XLSX.writeFile(wb, `Team_Performance_${ts}.xlsx`);
      toast.success(`Exported ${teamAnalytics.length} team members to Excel`, { id: "gph-export" });
    } catch (e) {
      toast.error(e.message || "Export failed", { id: "gph-export" });
    }
  }, [api, start, end, member]);

  const pages = Math.max(1, Math.ceil(total / limit));
  return { rows, total, loading, page, pages, setPage, pageSize: limit, exportPeople };
}

export default useGraphicalPeople;
